// libs
import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {usePagination, useSortBy, useTable} from "react-table";
import {Col, CustomInput, Input, Row, Table} from "reactstrap";

// hoc
import Aux from "../../../hoc/Auxiliary";

// components

const ReadingTable = (props) => {
	const {
		columns,
		data,
		checked,
		showExportXls,
		doXLSExport,
		sortField,
		sortDirection,
		onPage,
		initialPageIndex,
		setSortByCallback
	} = props;
	const {t, i18n} = useTranslation();

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		prepareRow,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		rows,
		state: {pageIndex, pageSize},
	} = useTable(
		{
			columns,
			data: data,
			rows,
			initialState: {
				pageIndex: initialPageIndex ? initialPageIndex : 0,
				pageSize: 10,
				sortBy: [{id: sortField, desc: sortDirection === 'DESC' ? true : false}],
			},
			disableSortRemove: true,
		},
		useSortBy,
		usePagination
	);


	useEffect(() => {
		onPage && onPage(pageIndex);
	}, [pageIndex]);

	const generateSortingIndicator = (column) => {
		return column.isSorted ? (
			column.isSortedDesc ? (
				<i className="fa fa-angle-down"></i>
			) : (
				<i className="fa fa-angle-up"></i>
			)
		) : (
			""
		);
	};

	const handlePageSize = (event) => {
		setPageSize(Number(event.target.value));
	};

	const handlePageChange = (event) => {
		const page = event.target.value ? Number(event.target.value) - 1 : 0;
		gotoPage(page);
	};

	return (
		<>
			<div className="flex flex-row flex-wrap justify-content-between">
				<div className="btn-export-xls">
					{showExportXls ? (
						<>
							<img
								className="specialarrow"
								src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB0AAAAVCAYAAAC6wOViAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAETSURBVHgB7ZYxjoJAFIbfoPRuSWg4woRAQrd6AvcKewNvIJ5g9wp7hD2B2tFA8AZ2tPQE8H8JNhMxg77Exi+BeUzmn28oGEbRHeI41l3X7VEuSIZDnucr596Itm3XgkLq+z7gdm45vsYCNvQ8J2spVliXZflHQjj0At7St1SEeRRFG3wSa9d1v7MsO08JP5p1ENqiXTZNs0+SJLANQpgi+zNkP2kCLN0NdWArHoS8WN44SuxW/zQBpyiKXwRTW7EpxA9hhd2qpilSvkG8sxFLCJnZtaiq6uh5HimllnhcYMIv1DyhxlX7vv8hIWSU2RGG4RaydCzwrJCZmR3GG4sLb0rHxFLCUakplhRaobUWO65cuQA3T9LjPVThWAAAAABJRU5ErkJggg=="
							/>
							<div className={"button"}>
								<button
									disabled={checked && checked.length === 0}
									className={`btn btn-secondary`}
									type="button"
									onClick={doXLSExport}
								>
									{t("docs.invoices.table.xls_download")}
									&nbsp;&nbsp;
									<i className="far fa-file-spreadsheet"></i>
								</button>

							</div>
						</>
					) : null}
				</div>
			</div>
			<div className="nopadding overflow-hidden w-100 flex flex-column">
				<Table
					hover
					{...getTableProps()}
					style={{minWidth: "100%"}}
					className="datatable datatable__responsive table-responsive w-100 "
				>
					<thead className="">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>
									<div {...column.getSortByToggleProps({title: t(`docs.togglesortby`) + ' ' + column.render("Header")})}>
										{column.render("Header")}
										{generateSortingIndicator(column)}
									</div>
								</th>
							))}
						</tr>
					))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{page.map((row) => {
							prepareRow(row);
							return (
								<Aux key={row.getRowProps().key}>
									<tr>
										{row.cells.map((cell) => {
											return (
												<td {...cell.getCellProps()}>{cell.render("Cell")}</td>
											);
										})}
									</tr>
								</Aux>
							);
						})}
					</tbody>
				</Table>
			</div>
			<div className="datatable__actions">
				<div className="datatable__actions__extras">
					<Row className="flex--align-center">
						<Col xs={6}>
							{t(`docs.pagination.page`)}{" "}
							<strong>
								{pageIndex + 1} {t(`docs.pagination.of`)} {pageOptions.length === 0 ? pageOptions.length + 1 : pageOptions.length}
							</strong>
						</Col>
						<Col xs={6} className="a-right">
							<CustomInput
								type="select"
								value={pageSize}
								id="row-range-select"
								onChange={handlePageSize}
							>
								{[10, 20, 30, 40, 50].map((pageSize) => (
									<option key={pageSize} value={pageSize}>
										{t(`docs.pagination.show`)} {pageSize}
									</option>
								))}
							</CustomInput>
						</Col>
					</Row>
				</div>
				<div className="datatable__actions__base flex flex--justify-center">
					<button
						className="btn btn--table"
						onClick={() => gotoPage(0)}
						disabled={!canPreviousPage}
					>
						<i className="fas fa-angle-double-left"></i>
					</button>
					<button
						className="btn btn--table"
						onClick={previousPage}
						disabled={!canPreviousPage}
					>
						<i className="fas fa-angle-left"></i>
					</button>
					<div>
						<Input
							type="number"
							className="input-current-page"
							min={1}
							style={{width: 70}}
							max={pageOptions.length}
							value={pageIndex + 1}
							onChange={handlePageChange}
						/>
					</div>
					<button
						className="btn btn--table"
						onClick={nextPage}
						disabled={!canNextPage}
					>
						<i className="fas fa-angle-right"></i>
					</button>
					<button
						className="btn btn--table"
						onClick={() => gotoPage(pageCount - 1)}
						disabled={!canNextPage}
					>
						<i className="fas fa-angle-double-right"></i>
					</button>
				</div>
			</div>
		</>
	);
};

export default ReadingTable;
